/**
 * Form helpers for create/edit activity views.
 */
import type { SelectOption } from '@/components/forms/Select';
import { getActivityTypes } from '@/services/activity-types.service';
import type { Activity, ActivityFormValues, CreateActivityDto } from '@/types/activity.types';

export type { ActivityFormValues };
import { DEFAULT_CREATE_ACTIVITY_FORM } from './activity-configs';
import { getErrorMessage } from '@/utils/errors-utils';

/** Values accepted by {@link applyActivityFormField}. */
type ActivityFormFieldValue = string | number | boolean | undefined;

/** Fresh create DTO from {@link DEFAULT_CREATE_ACTIVITY_FORM}. */
export function emptyCreateActivityForm(): CreateActivityDto {
  return { ...DEFAULT_CREATE_ACTIVITY_FORM };
}

/** Load the activity-type catalog (unwraps the Axios body). */
export async function fetchActivityTypes() {
  const response = await getActivityTypes();
  return response.data;
}

/** Map catalog rows to {@link Select} options. */
export function mapActivityTypeOptions(
  types: Array<{ id: string; label: string }>,
): SelectOption[] {
  return types.map((type) => ({
    value: type.id,
    label: type.label,
  }));
}

/** Label for a type id, or undefined when the catalog has not loaded it. */
export function resolveActivityTypeName(
  types: Array<{ id: string; label: string }>,
  activityTypeId: string | undefined,
): string | undefined {
  return types.find((type) => type.id === activityTypeId)?.label || undefined;
}

/** Immutable patch of one form field. */
export function applyActivityFormField<T extends object>(
  formData: T,
  field: keyof T,
  value: ActivityFormFieldValue,
): T {
  return { ...formData, [field]: value };
}

/** Hydrate the edit form from a GET activity. */
export function activityToFormData(activity: Activity): ActivityFormValues {
  return {
    activityTypeId: activity.activityTypeId,
    code: activity.code ?? '',
    label: activity.label,
    description: activity.description || '',
    status: activity.status ?? 'active',
  };
}

/** Attach `activityTypeName` so the API can store a denormalized label. */
export function buildActivitySubmitDto<T extends { activityTypeId?: string }>(
  formData: T,
  types: Array<{ id: string; label: string }>,
): T & { activityTypeName?: string } {
  return {
    ...formData,
    activityTypeName: resolveActivityTypeName(types, formData.activityTypeId),
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getActivityApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
